import React from "react";
import {
  Box,
  Card,
  Typography,
  useTheme,
  Tooltip,
  IconButton,
  Button,
  Collapse,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { MetricStatValue } from "../../../shared/api/types";
import { formatStatValue, formatTrendPercent, getRagColor } from "../../../shared/utils";
import { MetricInfoTooltipContent } from "./MetricInfoTooltip";

interface StatCardProps {
  metric: MetricStatValue;
  icon: React.ComponentType<SvgIconProps>;
  onClick?: () => void;
  /** When true, show a "Breakdown" toggle that reveals `breakdown` inside this card. */
  expandable?: boolean;
  /** Controlled open state for the inline breakdown. */
  expanded?: boolean;
  onToggleExpand?: () => void;
  /** Content rendered in the Collapse below the stat when expanded. */
  breakdown?: React.ReactNode;
}

export const StatCard: React.FC<StatCardProps> = ({
  metric,
  icon: IconComponent,
  onClick,
  expandable = false,
  expanded = false,
  onToggleExpand,
  breakdown,
}) => {
  const theme = useTheme();
  const normalizedLabel = metric.label.toLowerCase().trim();
  const isRestingHrMetric =
    normalizedLabel.includes("resting hr") ||
    normalizedLabel.includes("resting heart rate");
  const isRespiratoryRateMetric =
    normalizedLabel.includes("respiratory rate");
  const isRespiratoryInHealthyRange =
    typeof metric.value === "number" && metric.value >= 12 && metric.value <= 20;
  const isAvgSleepStressMetric = normalizedLabel.includes("avg high sleep stress");
  const isAvgStressScoreMetric = normalizedLabel.includes("stress score");
  // RAG colour may be absent (metrics with no agreed band, e.g. HRV) — fall
  // back to a neutral icon colour rather than implying a judgement.
  const color = metric.rag_color
    ? getRagColor(metric.rag_color, theme)
    : theme.palette.text.secondary;
  const normalizedChange = formatTrendPercent(metric.change) ?? "";
  const isNegativeChange = normalizedChange.trim().startsWith("-");
  const isPositiveChange = normalizedChange.trim().startsWith("+");
  const trendColor = isRespiratoryRateMetric
    ? isRespiratoryInHealthyRange
      ? theme.palette.success.main
      : theme.palette.error.main
    : isRestingHrMetric || isAvgSleepStressMetric || isAvgStressScoreMetric
      ? isNegativeChange
        ? theme.palette.success.main
        : isPositiveChange
          ? theme.palette.error.main
          : theme.palette.text.secondary
      : isNegativeChange
        ? theme.palette.error.main
        : isPositiveChange
          ? theme.palette.success.main
          : theme.palette.text.secondary;
  const TrendIcon = isNegativeChange
    ? TrendingDownIcon
    : isPositiveChange
      ? TrendingUpIcon
      : TrendingFlatIcon;

  return (
    <Card
      onClick={onClick}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        boxShadow: "none",
        cursor: onClick ? "pointer" : "default",
        transition: onClick ? "transform 0.15s ease, box-shadow 0.15s ease" : undefined,
        "&:hover": onClick
          ? {
              boxShadow: 2,
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <IconComponent fontSize="large" sx={{ color }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            {metric.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                variant="h5"
                component="span"
                sx={{
                  fontWeight: 700,
                }}
              >
                {formatStatValue(metric.value)}
              </Typography>
              {metric.unit && (
                <Typography variant="caption" color="text.secondary">
                  {metric.unit}
                </Typography>
              )}
            </Box>
            {metric.change && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  color: trendColor,
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  }}
                >
                  {normalizedChange}
                </Typography>
                <TrendIcon
                  sx={{
                    fontSize: "0.875rem",
                    color: trendColor,
                  }}
                />
                <Tooltip
                  title={
                    <MetricInfoTooltipContent
                      metricKey={metric.label}
                      fallbackText={
                        metric.rag_insight ??
                        `The average ${metric.label.toLowerCase()}${
                          metric.unit ? ` (${metric.unit})` : ""
                        } across the selected team(s) over the chosen period.`
                      }
                      trendNote={
                        <>
                          The {normalizedChange ? `${normalizedChange} ` : ""}change
                          compares this period's average with the previous
                          equal-length period
                          {typeof metric.previous_period_value === "number"
                            ? ` (which averaged ${metric.previous_period_value.toFixed(2)}${
                                metric.unit ? ` ${metric.unit}` : ""
                              })`
                            : ""}
                          .
                        </>
                      }
                    />
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    sx={{
                      p: 0,
                      ml: 0.25,
                      color: "text.disabled",
                      "&:hover": { color: "text.secondary" },
                    }}
                    aria-label="Change calculation info"
                  >
                    <InfoOutlinedIcon sx={{ fontSize: "0.75rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      {expandable && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Button
            size="small"
            variant="text"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: expanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              />
            }
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand?.();
            }}
            sx={{ fontSize: "0.7rem", textTransform: "none", color: "text.secondary" }}
          >
            {expanded ? "Hide breakdown" : "Breakdown"}
          </Button>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ borderTop: "1px solid", borderColor: "divider", pt: 1.5, mt: 0.5 }}>
              {breakdown}
            </Box>
          </Collapse>
        </Box>
      )}
    </Card>
  );
};
